import { useEffect, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { getODPDetails } from "api/individual-user-management";
import {
  IGetODPDetailsPayload,
  IUserODPDetails,
} from "api/individual-user-management/types";
import { BusinessContentItem } from "features/business-compliance/selected-business/BusinessContentItem";
import { ComplianceStatus } from "features/business-compliance/selected-business/ComplianceStatus";
import { EmptySelectedIndividual } from "features/individual-compliance/selected-individual/EmptySelectedIndividual";
import { IIndividualContentItem } from "features/individual-compliance/selected-individual/types";
import { useElementWidth } from "hooks/useElementWidth";
import { useParams } from "react-router-dom";
import { formatDateToDDMMYYTTSS, handleAmountFormat } from "utils";

// import { customErrorToast } from "ui/custom-toast";
import { Spinner } from "ui/spinner";
import { Heading4 } from "ui/typography";

// import { ONE } from "../../consts";
import { MoreQuickActions } from "./more-quick-actions";

const LG_WIDTH = 745;
const MD_WIDTH = 600;
const DEFAULT_USER_ODP_DETAILS: IUserODPDetails = {
  totalWithdrawalAmount: 0,
  totalWithdrawalFees: 0,
  jobTitle: "",
  companyName: "",
  status: "",
  netSalary: 0,
  dateEnrolled: "",
};

const SelectedOnDemandPay = () => {
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const isContentWrapperBelow745 = useElementWidth(contentWrapperRef, LG_WIDTH);
  const isContentWrapperBelow600 = useElementWidth(contentWrapperRef, MD_WIDTH);

  const { individualId: userId } = useParams();

  const [isLoading, setIsLoading] = useState(false);

  const [userODPDetails, setUserODPDetails] = useState<IUserODPDetails>(
    DEFAULT_USER_ODP_DETAILS
  );

  const handleGetODPDetails = async (payload: IGetODPDetailsPayload) => {
    try {
      setIsLoading(true);
      const { error, data } = await getODPDetails(payload);
      if (error) {
        console.error("Error: ", error);
      } else {
        setUserODPDetails(data);
      }
    } catch (error) {
      console.error("Error: ", error);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (!userId) return;
    void handleGetODPDetails({ userId });
  }, [userId]);

  const {
    totalWithdrawalAmount,
    totalWithdrawalFees,
    jobTitle,
    companyName,
    status,
    netSalary,
    dateEnrolled,
  } = userODPDetails;
  const userDetails: IIndividualContentItem[] = [
    {
      title: "Total Withdrawal Amount",
      content: (
        <Box className="font-semibold">
          {totalWithdrawalAmount
            ? handleAmountFormat({
                value: String(totalWithdrawalAmount),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })
            : "N/A"}
        </Box>
      ),
    },
    {
      title: "Total Transaction Fees",
      content: (
        <Box className="font-semibold">
          {totalWithdrawalFees
            ? handleAmountFormat({
                value: String(totalWithdrawalFees),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })
            : "N/A"}
        </Box>
      ),
    },
    {
      title: "Company Name",
      content: companyName,
    },
    {
      title: "Job Title",
      content: jobTitle,
    },
    {
      title: "Status",
      content: (
        <ComplianceStatus
          status={status === "active" ? "enrolled" : "not-started"}
          text={status === "active" ? "Enrolled" : "Not Started"}
          className="w-fit"
        />
      ),
    },
    {
      title: "Net Salary",
      content: (
        <Box className="font-semibold">
          {netSalary
            ? handleAmountFormat({
                value: String(netSalary),
                includeNairaSymbol: true,
                shouldFormatZero: true,
              })
            : "N/A"}
        </Box>
      ),
    },
    {
      title: "Date Enrolled",
      content: (
        <Box className="font-semibold">
          {dateEnrolled ? formatDateToDDMMYYTTSS(dateEnrolled) : "N/A"}
        </Box>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box className="w-full h-[340px] flex flex-col items-center justify-center">
        <Spinner size="20px" />
      </Box>
    );
  }
  return (
    <Box className="rounded-[20px] bg-white p-6">
      <Box className="flex justify-between mb-4 items-center">
        <Heading4
          fontWeight="700"
          lineHeight="24px"
          textColor="#00171A"
        >
          On-Demand Pay Details
        </Heading4>
        <Box>
          <MoreQuickActions
            withdrawal={totalWithdrawalAmount}
            fee={totalWithdrawalFees}
          />
        </Box>
      </Box>
      {companyName ? (
        <Box className="flex flex-col gap-6 lg:w-5/6">
          <Box>
            <Box
              css={{
                display: "flex",
                flexWrap: "wrap",
                columnGap: "32px",
                rowGap: "16px",
              }}
            >
              {userDetails.map((verificatioResult, index) => (
                <BusinessContentItem
                  key={index}
                  title={verificatioResult.title}
                  content={verificatioResult.content}
                  isContentWrapperBelow745={isContentWrapperBelow745}
                  isContentWrapperBelow600={isContentWrapperBelow600}
                />
              ))}
            </Box>
          </Box>
        </Box>
      ) : (
        <EmptySelectedIndividual
          padding="100px 24px"
          message="There is nothing to see here at the moment. Please check back later."
        />
      )}
    </Box>
  );
};

export default SelectedOnDemandPay;
